import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "theme/icons/CloseIcon";
import AgencyIcon from "theme/icons/AgencyIcon";
import { borderRadius, colors, fontSize, fontWeight, gradients, iconSize } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
// import { useCompanyContext, useCompanyContextActions } from "utils/context/CompanyContext";
// import { useGlobalAccess } from "utils/context/GlobalAccessContext";
// import { useAgencyStates } from "utils/context/AgencyStatesContext";
  // import { useSignatureDefaults } from "utils/context/SignatureDefaultsContext";
import { SignatureImageAgencyLocation } from "entities/UIModel/SignatureImageAgencyLocation";
import { axiosSecuredInstance } from "configurations/axiosConfig";

type SignatureAllViewType = "agency" | "state" | "location";

interface Props {
  open: boolean;
  type: SignatureAllViewType | null;
  onClose: () => void;
  signatureName: string;
  signatureAgencyId?: any;
}

const StyledDialog = styled(StewartDialog)({
  "& .MuiDialog-paper": {
    maxHeight: "95vh",
    maxWidth: "50vw",
    minWidth: "45vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
});

const Footer = styled(Box)({
  padding: "20px 32px",
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px",
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& svg": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  marginLeft: "auto",
  width: "32px",
  height: "32px",
  borderRadius: borderRadius.xlarge1,
  background: colors.grey10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  columnGap: "24px",
  padding: "28px 32px 20px 32px",
});

// const TitleBlock = styled(Box)({
//   display: "flex",
//   flexDirection: "column",
//   rowGap: "8px",
// });

const TitleText = styled(Typography)({
  fontSize: fontSize.xlarge1,
  fontWeight: fontWeight.bold1,
  textTransform: "capitalize",
});

const ContentBox = styled(Box)({
  border: `1px solid ${colors.grey04}`,
  borderRadius: borderRadius.medium,
  margin: "0 32px 24px 32px",
  padding: "16px 20px",
  height: "320px",
  overflowY: "auto",
});

const BulletList = styled("ul")({
  margin: 0,
  paddingLeft: "20px",
  listStyleType: "disc",
  "& li": {
    marginBottom: "12px",
  },
});

const SignatureAllViewModal: React.FC<Props> = ({
  open,
  type,
  onClose,
  signatureName,
  signatureAgencyId,
}) => {
  const [data, setData] = useState<SignatureImageAgencyLocation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !signatureAgencyId) return;

    const controller = new AbortController();
    setLoading(true);

    axiosSecuredInstance
      .get(
        `/Signature/v2/Defaults/GetAgencyLocationsByImage/${signatureAgencyId}`,
        { signal: controller.signal }
      )
      .then((res) => {
        const mapped = res.data.map((l: any) => ({
          agencyID: l.AgencyID,
          agencyName: l.AgencyName,
          locationDisplayName: l.LocationDisplayName,
          locationID: l.LocationID,
          locationUniqueID: l.LocationUniqueID,
          stateAbbr: l.StateAbbr,
          stateCode: l.StateCode,
        }));

        setData(mapped);
      }).catch((error) => {
        if (error.name === "CanceledError") return;
        console.error(error);
      })
      
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [open, signatureAgencyId]);

  const list = useMemo(() => {
    if (!type) return [];

    const agencies = new Map<string, string>();
    const states = new Set<string>();
    const locations = new Set<string>();

    for (const item of data) {
      agencies.set(item.agencyID, item.agencyName);
      states.add(item.stateAbbr);
      locations.add(item.locationDisplayName);
    }

    if (type === "agency") return Array.from(agencies.values());
    if (type === "state") return Array.from(states);
    return Array.from(locations);
  }, [data, type]);

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <IconContainer>
          <AgencyIcon />
        </IconContainer>

        <TitleText>
          View All  {type === "agency" ? "Agencies" : `${type}s`} for {signatureName} Default Signature
        </TitleText>

        <CloseIconContainer>
          <IconButton size="small" onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <ContentBox>
        {loading ? (
          <Typography color="text.secondary">Loading...</Typography>
        ) : list.length ? (
          <BulletList>
            {list.map((text) => (
              <li key={text}>
                <Typography>{text}</Typography>
              </li>
            ))}
          </BulletList>
        ) : (
          <Typography color="text.secondary">No data available</Typography>
        )}
      </ContentBox>

      <Footer>
        <ActionButton variant="outlined" color="secondary" onClick={onClose}>
          Done
        </ActionButton>
      </Footer>
    </StyledDialog>
  );
};


export default SignatureAllViewModal;
